import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { db } from '../lib/firebase';
import { supabase } from '../lib/supabase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, setDoc, collectionGroup } from 'firebase/firestore';
import { CircleCheck as CheckCircle2, ChevronRight, Users, Layers, Zap, Tablet, User, RotateCw, Check, TriangleAlert as AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import clsx from 'clsx';

export const PublicRegistration = () => {
    const params = useParams();
    const [searchParams] = useSearchParams();
    const { t, isRtl } = useLanguage();
    
    // Robust parameter extraction
    const rawS = params.sessionId || searchParams.get('s') || '';
    const rawP = (searchParams.get('phone') || searchParams.get('p') || '').replace(/\D/g, ''); 
    const rawHost = params.hostId || searchParams.get('h') || '';

    const [game, setGame] = useState(null);
    const [hostId, setHostId] = useState(rawHost);
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [nameInput, setNameInput] = useState('');
    const [phoneInput, setPhoneInput] = useState('');
    const [step, setStep] = useState(0); // 0: loading, 'form': new player, 1: name only, 2: choose table
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const STORAGE_KEY = 'reshima_player';

    const savePlayerLocally = (phone, name) => {
        if (!phone) return;
        const data = JSON.stringify({ phone, name });
        try { localStorage.setItem(STORAGE_KEY, data); } catch {}
        try {
            const expires = new Date();
            expires.setFullYear(expires.getFullYear() + 1);
            document.cookie = `${STORAGE_KEY}=${encodeURIComponent(data)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
        } catch {}
    };

    const loadStoredPlayer = () => {
        try {
            const ls = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
            if (ls?.phone) return ls;
        } catch {}
        try {
            const match = document.cookie.match(new RegExp(`${STORAGE_KEY}=([^;]+)`));
            if (match) return JSON.parse(decodeURIComponent(match[1]));
        } catch {}
        return null;
    };

    // Initial Data Load & Intelligence Gate
    useEffect(() => {
        const load = async () => {
            console.log('[Registration] Phase 1: Booting gate...', { s: rawS, p: rawP, host: rawHost });
            
            if (!rawS) {
                console.error('[Registration] Critical failure: No Session ID provided.');
                setError(t('missingSessionId'));
                setLoading(false);
                return;
            }

            try {
                let currentHostId = rawHost;
                
                // 1. Resolve Session Context (Cross-tenant lookup)
                if (!currentHostId) {
                    console.log('[Registration] Host unknown. Searching for session context:', rawS);
                    const cleanSid = rawS.replace('session_', '').trim();
                    
                    try {
                        // Use collectionGroup to find the host that owns this session
                        const q = query(collectionGroup(db, 'sessions'), where('gameId', '==', cleanSid));
                        const snap = await getDocs(q);
                        
                        if (snap.empty) {
                            // Try fallback with raw SID
                            console.log('[Registration] cleanSid index empty. Trying raw fallback:', rawS);
                            const q2 = query(collectionGroup(db, 'sessions'), where('gameId', '==', rawS));
                            const snap2 = await getDocs(q2);
                            if (!snap2.empty) {
                                const pathParts = snap2.docs[0].ref.path.split('/');
                                currentHostId = pathParts[1];
                            }
                        } else {
                            const pathParts = snap.docs[0].ref.path.split('/');
                            currentHostId = pathParts[1];
                        }
                    } catch (collectionGroupErr) {
                        console.error('[Registration] CollectionGroup query failed. This usually means a missing index or rule.', collectionGroupErr);
                        // If it's a permission error or index error, we can't resolve hostId automatically.
                        if (collectionGroupErr.code === 'permission-denied') {
                             throw new Error(t('internalError'));
                        }
                        if (collectionGroupErr.message?.includes('index')) {
                             throw new Error(t('internalError'));
                        }
                        throw collectionGroupErr;
                    }
                }

                if (!currentHostId) {
                    console.error('[Registration] Session resolution failed: Host could not be determined.');
                    setError(t('couldNotFindGame'));
                    setLoading(false);
                    return;
                }

                console.log('[Registration] Session resolved to Host:', currentHostId);
                setHostId(currentHostId);

                // 2. Fetch Game Logic
                const gamePaths = [
                    `hosts/${currentHostId}/games/${rawS}`,
                    `hosts/${currentHostId}/games/${rawS.replace('session_', '')}`
                ];

                let gameDoc = null;
                for (const path of gamePaths) {
                    const snap = await getDoc(doc(db, path));
                    if (snap.exists()) {
                        gameDoc = { id: snap.id, ...snap.data() };
                        break;
                    }
                }

                if (!gameDoc) {
                    setError(t('couldNotFindGame'));
                    setLoading(false);
                    return;
                }
                setGame(gameDoc);

                // 3. Resolve phone — URL param takes priority, then localStorage cookie
                const stored = loadStoredPlayer();
                const resolvedPhone = rawP || stored?.phone || '';

                if (!resolvedPhone) {
                    setStep('form');
                    setLoading(false);
                    return;
                }

                // 4. Silent Player Lookup
                const pq = query(collection(db, `hosts/${currentHostId}/players`), where("phone", "==", resolvedPhone));
                const pSnap = await getDocs(pq);

                if (!pSnap.empty) {
                    const pData = { id: pSnap.docs[0].id, ...pSnap.docs[0].data() };
                    setPlayer(pData);
                    savePlayerLocally(pData.phone || resolvedPhone, pData.name);
                    setStep(2);
                } else if (stored?.name) {
                    // Phone in cookie but not in this host's DB — pre-fill name, skip to name-only confirm
                    setNameInput(stored.name);
                    setStep(1);
                } else {
                    setStep(1); // Ask for name only (phone already known)
                }

            } catch (err) {
                console.error('[Registration Internal Crash]', err);
                setError(t('internalError'));
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [rawS, rawP, rawHost]);

    const handleIdentify = async (e) => {
        if (e) e.preventDefault();
        const trimmedName = nameInput.trim();
        const stored = loadStoredPlayer();
        // Phone comes from: URL param → localStorage → form input
        const resolvedPhone = (rawP || stored?.phone || phoneInput.replace(/\D/g, '')).trim();

        if (!trimmedName || !resolvedPhone || !hostId) return;

        setSubmitting(true);

        try {
            // Find or create player by phone (phone is the canonical identifier)
            const pq = query(collection(db, `hosts/${hostId}/players`), where("phone", "==", resolvedPhone));
            const pSnap = await getDocs(pq);

            let pData;
            if (pSnap.empty) {
                const newRef = doc(collection(db, `hosts/${hostId}/players`));
                pData = {
                    id: newRef.id,
                    name: trimmedName,
                    phone: resolvedPhone,
                    registrationStatus: 'registered',
                    createdAt: new Date().toISOString(),
                    role: 'Player'
                };
                await setDoc(newRef, pData);
            } else {
                pData = { id: pSnap.docs[0].id, ...pSnap.docs[0].data() };
                if (pData.phone !== resolvedPhone) {
                    await updateDoc(doc(db, `hosts/${hostId}/players/${pData.id}`), { phone: resolvedPhone });
                    pData.phone = resolvedPhone;
                }
            }

            // Persist to device so future links skip this form
            savePlayerLocally(resolvedPhone, trimmedName);
            setPlayer(pData);

            // Rewrite URL with ?p=phone so it persists in browser history
            // (helps WhatsApp WebView which isolates localStorage/cookies)
            try {
                const url = new URL(window.location.href);
                url.searchParams.set('p', resolvedPhone);
                window.history.replaceState(null, '', url.toString());
            } catch {}

            // Supabase Sync
            try {
                const { data: sPlayer, error: sError } = await supabase
                    .from('players')
                    .upsert({ name: trimmedName, phone: resolvedPhone }, { onConflict: 'phone' })
                    .select().single();
                if (sError) console.warn('[Supabase Player Sync Error]', sError);
            } catch (err) {
                console.error('[Supabase Sync Exception]', err);
            }

            setStep(2);
        } catch (err) {
            console.error('[Registration Identify Failed]', err);
            setError(t('internalError'));
        } finally {
            setSubmitting(false);
        }
    };

    const registerToTable = async (tIndex) => {
        if (!player || !game || !hostId || submitting) return;

        setSubmitting(true);
        setError('');
        console.log('[Registration] Assigning seat at Table', tIndex);

        try {
            const sessionKey = tIndex === 2 ? game.ledgerId2 : game.ledgerId;
            if (!sessionKey) throw new Error('Session Ledger Mapping Missing');

            const ledgerRef = doc(db, `hosts/${hostId}/sessions/${sessionKey}`);
            const lSnap = await getDoc(ledgerRef);
            
            if (!lSnap.exists()) throw new Error('Ledger Not Found');

            const lData = lSnap.data();
            const seats = { ...(lData.tableSeats || {}) };

            // Check if player is already registered in this session
            const alreadySeated = Object.values(seats).some(
                s => s && (s.id === player.id || (player.phone && s.phone === player.phone))
            );
            if (alreadySeated) {
                setSuccess(true); // treat as success — they're already in
                setSubmitting(false);
                return;
            }

            // Find free seat (1-11)
            let seat = null;
            for (let i = 1; i <= 11; i++) {
                if (!seats[i]) { seat = i; break; }
            }

            if (!seat) {
                setError(t('tableFull'));
                setSubmitting(false);
                return;
            }

            // Update Ledger DB
            const updatedSeats = {
                ...seats,
                [seat]: {
                    id: player.id,
                    name: player.name,
                    phone: player.phone || rawP,
                    registrationTime: new Date().toISOString()
                }
            };

            await updateDoc(ledgerRef, { tableSeats: updatedSeats });

            // Supabase Registration (Agent 1: Live Feed Trigger)
            try {
                // Get Supabase Player ID first
                const { data: sPlayer } = await supabase
                    .from('players')
                    .select('id')
                    .eq('phone', player.phone || rawP)
                    .single();

                if (sPlayer) {
                    await supabase.from('session_registrations').insert({
                        session_id: rawS,
                        player_id: sPlayer.id,
                        status: 'registered'
                    });
                }
            } catch (err) {
                console.error('[Supabase Registration Error]', err);
            }

            // Sync Game Stats
            const gameRef = doc(db, `hosts/${hostId}/games/${game.id}`);
            const count = Object.values(updatedSeats).filter(Boolean).length;
            await updateDoc(gameRef, { [tIndex === 2 ? 't2Players' : 't1Players']: count });

            // BRAIN WEBHOOK (Async, Fire-and-Forget)
            const webhookUrl = 'https://api-brain.reshima.io/webhook/confirm-registration';
            fetch(webhookUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    phone: player.phone || rawP,
                    name: player.name,
                    session_id: rawS
                })
            }).catch(e => console.error('[Webhook Silent Error]', e));

            savePlayerLocally(player.phone || rawP, player.name);
            setSuccess(true);
        } catch (err) {
            console.error('[Registration Final Error]', err);
            setError(t('internalError'));
        } finally {
            setSubmitting(false);
        }
    };

    // UI RENDERING
    if (loading) return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
            <RotateCw className="w-12 h-12 text-[#7C3AED] animate-spin mb-6" />
            <p className="text-slate-400 font-bold text-lg animate-pulse tracking-widest uppercase">{t('initializingSecureGate')}</p>
        </div>
    );

    if (error && !game) return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="w-20 h-20 bg-rose-500/10 border border-rose-500/20 rounded-[2rem] flex items-center justify-center mb-8 rotate-3">
                <AlertTriangle className="w-10 h-10 text-rose-500" />
            </div>
            <h1 className="text-2xl font-black text-white mb-3">{t('registrationFailure')}</h1>
            <p className="text-slate-500 text-lg mb-8 max-w-xs">{error}</p>
            <button onClick={() => window.location.reload()} className="px-10 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-3xl text-white font-black transition-all">
                {t('tryAgain')}
            </button>
        </div>
    );

    if (success) return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-8 text-center animate-in zoom-in duration-500" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="w-28 h-28 bg-[#7C3AED]/20 rounded-[3rem] flex items-center justify-center mb-10 shadow-[0_0_60px_rgba(124,58,237,0.2)] border border-[#7C3AED]/30 rotate-12">
                <Check className="w-14 h-14 text-[#7C3AED]" />
            </div>
            <h1 className="text-4xl font-black text-white mb-4 tracking-tighter">{t('seeYouAtTheTable')}</h1>
            <p className="text-slate-400 text-xl font-medium mb-12">{t('placeSavedSuccessfully')}</p>
            
            <div className="bg-slate-900/50 backdrop-blur-3xl border border-white/5 rounded-[3.5rem] p-12 w-full max-w-sm relative overflow-hidden shadow-2xl">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#7C3AED]/10 blur-[50px] rounded-full -mr-16 -mt-16" />
                <div className={clsx("space-y-6", isRtl ? "text-right" : "text-left")}>
                    <div>
                        <div className="text-[10px] text-slate-600 uppercase font-black tracking-[0.3em] mb-2">{t('playerProfile')}</div>
                        <div className="text-2xl font-black text-white">{player?.name}</div>
                    </div>
                    <div className="flex justify-between items-center border-t border-white/5 pt-6">
                        <div className={isRtl ? "text-right" : "text-left"}>
                            <div className="text-[10px] text-slate-600 uppercase font-black tracking-[0.3em] mb-2">{t('gameStakes')}</div>
                            <div className="text-xl font-black text-[#7C3AED]">{game?.stakes}</div>
                        </div>
                        <div className="px-4 py-2 bg-emerald-500/10 rounded-xl text-[10px] font-black text-emerald-500 uppercase tracking-widest border border-emerald-500/20">
                            {t('seated')}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center p-6" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="w-full max-w-lg mt-12 bg-slate-900 border border-white/5 rounded-[4rem] p-12 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-80 h-80 bg-[#7C3AED]/5 blur-[100px] -mr-40 -mt-40 rounded-full" />
                
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-[#7C3AED]/10 rounded-[2rem] border border-[#7C3AED]/20 mb-8 shadow-2xl rotate-3">
                        <Zap className="w-10 h-10 text-[#7C3AED]" />
                    </div>
                    
                    <h1 className="text-4xl font-black text-white mb-3 tracking-tighter">
                        {player ? t('welcomeBack', { name: player.name }) : t('welcomeToReshima')}
                    </h1>
                    <p className="text-slate-500 font-bold text-lg leading-relaxed max-w-xs mx-auto">
                        {player
                            ? t('systemRecognizedYou')
                            : step === 1
                            ? t('whatIsYourName')
                            : t('enterDetailsQuickRegistration')}
                    </p>
                </div>

                {error && <div className="bg-rose-500/10 border border-rose-500/20 text-rose-500 text-sm px-6 py-5 rounded-3xl mb-8 text-center font-bold">{error}</div>}

                {/* Step form: First-time — name + phone */}
                {step === 'form' && (
                    <form onSubmit={handleIdentify} className="space-y-5 animate-in slide-in-from-bottom-4 duration-500">
                        <div className="space-y-3">
                            <label className={clsx("text-xs font-black text-slate-600 uppercase tracking-[0.3em]", isRtl ? "mr-2" : "ml-2")}>{t('fullNameNickname')}</label>
                            <div className="relative">
                                <User className={clsx("absolute top-1/2 -translate-y-1/2 w-5 h-5 text-slate-700", isRtl ? "right-6" : "left-6")} />
                                <input
                                    type="text"
                                    required
                                    value={nameInput}
                                    onChange={e => setNameInput(e.target.value)}
                                    placeholder={t('whatIsYourName')}
                                    className={clsx(
                                        "w-full bg-slate-800/50 border-2 border-transparent focus:border-[#7C3AED]/30 rounded-3xl py-5 text-white text-lg font-black outline-none transition-all",
                                        isRtl ? "pr-14 pl-6" : "pl-14 pr-6"
                                    )}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <div className="space-y-3">
                            <label className={clsx("text-xs font-black text-slate-600 uppercase tracking-[0.3em]", isRtl ? "mr-2" : "ml-2")}>{t('phoneNumber')}</label>
                            <input
                                type="tel"
                                required
                                value={phoneInput}
                                onChange={e => setPhoneInput(e.target.value)}
                                placeholder="05XXXXXXXX"
                                dir="ltr"
                                className="w-full bg-slate-800/50 border-2 border-transparent focus:border-[#7C3AED]/30 rounded-3xl px-6 py-5 text-white text-lg font-black outline-none transition-all tabular-nums"
                            />
                        </div>
                        <p className="text-[10px] text-slate-700 text-center font-black uppercase tracking-widest pt-1">
                            {t('savedOnDevice')}
                        </p>
                        <button
                            disabled={submitting}
                            className="w-full py-6 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-black text-xl rounded-[1.75rem] transition-all shadow-xl active:scale-95 disabled:opacity-50"
                        >
                            {submitting ? t('saving') : t('continueToTable')}
                        </button>
                    </form>
                )}

                {/* Step 1: Returning device, new to this host — name only */}
                {step === 1 && (
                    <form onSubmit={handleIdentify} className="space-y-8 animate-in slide-in-from-bottom-4 duration-500">
                        <div className="space-y-3">
                            <label className={clsx("text-xs font-black text-slate-600 uppercase tracking-[0.3em]", isRtl ? "mr-2" : "ml-2")}>{t('whatIsYourName')}</label>
                            <div className="relative">
                                <User className={clsx("absolute top-1/2 -translate-y-1/2 w-6 h-6 text-slate-700", isRtl ? "right-6" : "left-6")} />
                                <input
                                    type="text"
                                    required
                                    value={nameInput}
                                    onChange={e => setNameInput(e.target.value)}
                                    placeholder={t('fullNameNickname')}
                                    className={clsx(
                                        "w-full bg-slate-800/50 border-2 border-transparent focus:border-[#7C3AED]/20 rounded-3xl py-6 text-white text-xl font-black outline-none transition-all",
                                        isRtl ? "pr-16 pl-6" : "pl-16 pr-6"
                                    )}
                                    autoFocus
                                />
                            </div>
                        </div>
                        <button
                            disabled={submitting}
                            className="w-full py-6 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-black text-2xl rounded-[1.75rem] transition-all shadow-xl active:scale-95 disabled:opacity-50"
                        >
                            {submitting ? t('saving') : t('continueToTable')}
                        </button>
                    </form>
                )}

                {/* Step 2: Table Selection */}
                {step === 2 && (
                    <div className="grid gap-5 animate-in slide-in-from-bottom-4 duration-500">
                        <p className="text-[10px] text-slate-700 font-black uppercase tracking-[0.4em] mb-2 text-center">{t('availableSessions')}</p>
                        
                        <button 
                            onClick={() => registerToTable(1)}
                            disabled={submitting}
                            className={clsx(
                                "w-full bg-slate-800/30 border border-white/5 hover:border-[#7C3AED]/30 p-8 rounded-[3rem] shadow-xl group transition-all flex items-center justify-between",
                                isRtl ? "text-right" : "text-left"
                            )}
                        >
                            <div className="flex items-center gap-6">
                                <div className="w-14 h-14 bg-[#7C3AED]/10 rounded-2xl flex items-center justify-center border border-[#7C3AED]/20">
                                    <Tablet className="w-8 h-8 text-[#7C3AED]" />
                                </div>
                                <div className={isRtl ? "text-right" : "text-left"}>
                                    <div className="text-[10px] font-black text-[#7C3AED]/50 uppercase tracking-widest">{t('mainTable')}</div>
                                    <h2 className="text-2xl font-black text-white mb-1">{game?.stakes}</h2>
                                    <p className="text-slate-600 font-bold text-xs uppercase tracking-tighter">{game?.variant || 'No Limit Hold\'em'}</p>
                                </div>
                            </div>
                            <ChevronRight className={clsx("w-6 h-6 text-slate-800 group-hover:text-[#7C3AED]", isRtl && "rotate-180")} />
                        </button>

                        {game?.capacity === '2 Tables' && (
                            <button 
                                onClick={() => registerToTable(2)}
                                disabled={submitting}
                                className={clsx(
                                    "w-full bg-slate-800/30 border border-white/5 hover:border-blue-500/30 p-8 rounded-[3rem] shadow-xl group transition-all flex items-center justify-between",
                                    isRtl ? "text-right" : "text-left"
                                )}
                            >
                                <div className="flex items-center gap-6">
                                    <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center border border-blue-500/20">
                                        <Tablet className="w-8 h-8 text-blue-500" />
                                    </div>
                                    <div className={isRtl ? "text-right" : "text-left"}>
                                        <div className="text-[10px] font-black text-blue-500/50 uppercase tracking-widest">{t('table02')}</div>
                                        <h2 className="text-2xl font-black text-white mb-1">{game?.stakes}</h2>
                                        <p className="text-slate-600 font-bold text-xs uppercase tracking-tighter">{game?.variant || 'No Limit Hold\'em'}</p>
                                    </div>
                                </div>
                                <ChevronRight className={clsx("w-6 h-6 text-slate-800 group-hover:text-blue-500", isRtl && "rotate-180")} />
                            </button>
                        )}
                    </div>
                )}

                <div className="mt-12 pt-8 border-t border-white/5 flex items-center justify-center gap-2 opacity-50">
                    <div className="w-1.5 h-1.5 bg-[#7C3AED] rounded-full animate-pulse" />
                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest">{t('secureHandshakeEstablished')}</span>
                </div>
            </div>
        </div>
    );
};
